import styled from 'styled-components';
import CheckIcon from '@/assets/icon/Check.svg?react';
import InfoIcon from '@/assets/icon/Info.svg?react';
import { adjusters } from '@/data/PcAdjusters';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function AdjusterIntro() {
  const { id } = useParams();
  const adjuster = adjusters.find((a) => String(a.id) === id);

  if (!adjuster) {
    return <div>해당 손해사정사를 찾을 수 없습니다.</div>;
  }
  return (
    <>
      <Helmet>
        <title>{`${adjuster.name} 손해사정사 | 올받음`}</title>
        <meta name="description" content={adjuster.introduction.slice(0, 1000)} />
        <meta property="og:title" content={`${adjuster.name} 손해사정사 | 올받음`} />
        <meta property="og:description" content={adjuster.introduction.slice(0, 1000)} />
        <meta property="og:image" content={adjuster.image} />
      </Helmet>
      <Wrapper>
        <Container>
          <Title>손해사정사 소개</Title>
          <SubTitle>올받음 손해사정사를 소개합니다!</SubTitle>

          <Divider />

          <ContentWrapper>
            <CardRow>
              <LeftCard>
                <Image src={adjuster.image} alt={`${adjuster.name} 이미지`} />
              </LeftCard>

              <RightCard>
                <RightCardInner>
                  <ProfileWrapper>
                    <TopText>파트너 손해사정사</TopText>
                    <NameRow>
                      <Name>{adjuster.name}</Name>
                      <Tag>{adjuster.region}</Tag>
                    </NameRow>
                    <HashTags>{adjuster.strength}</HashTags>
                    <InfoBox>
                      <InfoItem>
                        <InfoLabel>손해사정사 등록 번호</InfoLabel>
                        <InfoValueBox>
                          <InfoValue>{adjuster.registrationNumber}</InfoValue>
                        </InfoValueBox>
                      </InfoItem>
                      <InfoItem
                        style={{ borderLeft: '1px solid var(--table-line-table_line04, #CFCFCF)' }}
                      >
                        <InfoLabel>보수교육 이수</InfoLabel>
                        <InfoValueBox>
                          <InfoValue>{adjuster.trainingCompleted ? '유' : '무'}</InfoValue>
                        </InfoValueBox>
                      </InfoItem>
                    </InfoBox>

                    <Message>{adjuster.introduction}</Message>

                    <ConsultWrapper>
                      <ConsultTitle>상담 시간</ConsultTitle>

                      <ConsultTable>
                        <ConsultRow>
                          <DayCell isHeader>시간</DayCell>
                          {['월', '화', '수', '목', '금'].map((day, idx) => (
                            <DayCell key={day} isLast={idx === 4}>
                              {day}
                            </DayCell>
                          ))}
                        </ConsultRow>
                        <TimeRow>
                          <TimeCell isFirst>
                            <TimeText>오전</TimeText>
                          </TimeCell>
                          {[...Array(5)].map((_, index) => (
                            <TimeCell key={index} isLast={index === 4}>
                              <IconWrapper>
                                <CheckIcon />
                              </IconWrapper>
                            </TimeCell>
                          ))}
                        </TimeRow>
                        <TimeRow>
                          <TimeDataCell isFirst>
                            <TimeText>오후</TimeText>
                          </TimeDataCell>
                          {[...Array(5)].map((_, index) => (
                            <TimeDataCell key={index} isLast={index === 4}>
                              <IconWrapper>
                                <CheckIcon />
                              </IconWrapper>
                            </TimeDataCell>
                          ))}
                        </TimeRow>
                        <TimeRow
                          style={{
                            display: 'flex',
                            height: '50px',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            background: 'var(--table-BG-table_bg02_hov, #F0F3FE)',
                            marginTop: '16px',
                          }}
                        >
                          <InfoIcon />
                          <NoticeText>
                            유의사항: 평일 상담 시간 9:00~17:00까지인 점 참고 바랍니다
                          </NoticeText>
                        </TimeRow>
                        <Divid />

                        <ExtraSection>
                          <ColumnBlock>
                            <BlockTitle>자격증</BlockTitle>
                            <ItemList>
                              {adjuster.certification
                                ?.split('\n')
                                .map((cert: string, i: number) => <li key={i}>{cert.trim()}</li>)}
                            </ItemList>
                          </ColumnBlock>
                          <Divid />
                          <ColumnBlock>
                            <BlockTitle>경력</BlockTitle>
                            <ItemList>
                              {adjuster.career
                                ?.split('\n')
                                .map((career: string, i: number) => (
                                  <li key={i}>{career.trim()}</li>
                                ))}
                            </ItemList>
                          </ColumnBlock>
                        </ExtraSection>
                      </ConsultTable>
                    </ConsultWrapper>
                  </ProfileWrapper>
                </RightCardInner>
              </RightCard>
            </CardRow>
          </ContentWrapper>
        </Container>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1240px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 156px;
  padding: 0 16px;

  @media (max-width: 1024px) {
    padding: 0 24px;
  }

  @media (max-width: 768px) {
    margin-top: 100px;
    padding: 0 16px;
  }
`;

const Title = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  text-align: center;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.64px;
`;

const SubTitle = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;
`;

const Divider = styled.hr`
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid #000;
  margin: 50px 0;
`;

const Divid = styled.hr`
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid #bdbdbd;
  margin: 50px 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  padding: 50px 0 192px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const CardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftCard = styled.div`
  display: flex;
  max-width: 624px;
  width: 100%;
  height: 473px;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const Image = styled.img`
  object-fit: cover;
`;

const RightCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 604.172px;
  flex-direction: column;

  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const RightCardInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 61px;
  align-self: stretch;
`;

const ProfileWrapper = styled.div`
  display: flex;
  padding-top: 72px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TopText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
  align-self: stretch;
  text-align: start;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const Name = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.52px;
`;

const Tag = styled.div`
  display: flex;
  padding: 0px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 99px;
  background: var(--Primary-lightgray, #f3f3f3);
  color: var(--Primary-darkgray, #000c24);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const HashTags = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray05, #999);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  text-align: start;
  margin-top: 12px;
  margin-bottom: 32px;
`;

const InfoBox = styled.div`
  display: flex;
  padding: 8px 0px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  background: var(--table-BG-table_bg02_hov, #f0f3fe);
`;

const InfoItem = styled.div`
  display: flex;
  padding: 0px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
`;

const InfoLabel = styled.div`
  align-self: stretch;
  color: var(--Primary-brand_02, #4c84e3);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const InfoValueBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InfoValue = styled.div`
  align-self: stretch;
  color: var(--Primary-brand_02, #4c84e3);
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const Message = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  margin-top: 16px;
  text-align: start;
  margin-bottom: 61px;
`;

const ConsultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ConsultTitle = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
  text-align: start;
`;

const ConsultTable = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ConsultRow = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

const DayCell = styled.div<{ isHeader?: boolean; isLast?: boolean }>`
  display: flex;
  flex: 1 1 0; // 비율 기반
  min-width: 60px; // 너무 작아지지 않도록
  height: 50px;
  padding: 16px 8px;
  justify-content: center;
  align-items: center;
  background: #0b46dd;
  color: var(--Primary-white, #fff);
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.28px;
  border-top-left-radius: ${(props) => (props.isHeader ? '16px' : '0')};
  border-top-right-radius: ${(props) => (props.isLast ? '16px' : '0')};
`;

const TimeRow = styled.div`
  display: flex;
  width: 100%;
  flexwrap: nowrap;
`;

const TimeCell = styled.div<{ isFirst?: boolean; isLast?: boolean }>`
  display: flex;
  flex: 1 1 0;
  min-width: 60px;
  padding: 16px 8px;
  justify-content: center;
  align-items: center;
  border-left: ${(props) =>
    props.isFirst ? '1px solid var(--table-line-table_line03, #D1D1D1)' : 'none'};
  border-right: ${(props) =>
    props.isLast ? '1px solid var(--table-line-table_line03, #D1D1D1)' : 'none'};
`;

const TimeDataCell = styled.div<{ isFirst?: boolean; isLast?: boolean }>`
  display: flex;
  flex: 1 1 0;
  min-width: 60px;
  padding: 16px 8px;
  justify-content: center;
  align-items: center;
  border-left: ${(props) =>
    props.isFirst ? '1px solid var(--table-line-table_line03, #D1D1D1)' : 'none'};
  border-right: ${(props) =>
    props.isLast ? '1px solid var(--table-line-table_line03, #D1D1D1)' : 'none'};
  border-bottom: 1px solid var(--table-line-table_line03, #d1d1d1);
  border-bottom-left-radius: ${(props) => (props.isFirst ? '16px' : '0')};
  border-bottom-right-radius: ${(props) => (props.isLast ? '16px' : '0')};
`;

const IconWrapper = styled.div`
  display: flex;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  img {
    width: 20px;
    height: 20px;
  }
`;

const TimeText = styled.div`
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const NoticeText = styled.div`
  color: var(--Primary-brand_02, #4c84e3);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.28px;
  text-align: start;
`;

const ExtraSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 50px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ColumnBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: start;
  align-self: stretch;
  gap: 16px;
  & > div,
  & > ul {
    width: 100%;
  }
`;

const BlockTitle = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.56px;
`;

const ItemList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding-left: 20px;

  li {
    align-self: stretch;
    color: var(--gray-ui_gray04, #6f6f6f);
    font-family: 'Spoqa Han Sans Neo';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: -0.32px;
  }
`;
